<script>
    // Apple guideline 5.1.1(v): an app that lets people create an account must
    // let them delete it from inside the app. There was no deletion path at
    // all, and the footer's "Delete My Data" opened a mail client.
    //
    // Reachable signed in or signed out, because a signed-out visitor has rows
    // on the server too: taking an analysis without an account stores a users
    // row and a progresspictures row against the random id in this browser.
    //
    // Deliberately not a modal. A modal over the profile would be dismissed by
    // a stray tap, and this is the one screen where the person should read a
    // list before they act.
    import { onMount } from 'svelte';
    import { get } from 'svelte/store';
    import { userStore } from '../store.js';
    import { navigate } from '../router.js';
    import Button from '../components/Button.svelte';
    import Card from '../components/Card.svelte';
    import Banner from '../components/Banner.svelte';

    // Local keys that hold the person's own data, including the marked-up
    // selfie. The server never had the photo, so this is the only place it
    // can be deleted from.
    const LOCAL_KEYS = [
        'base64Selfie',
        'originalBase64Selfie',
        'AoI',
        'AoS',
        'skinAllergies',
        'skinProfile',
        'formDataForRecommendations',
        'pendingRecommendationRequest',
        'currentRoutine',
        'userToken',
        'UserId'
    ];

    let step = 'intro';          // intro | confirming | deleting | done | error
    let signedIn = false;
    let userId = null;
    let username = '';
    let errorMessage = '';
    let identityKept = false;

    onMount(async () => {
        const local = get(userStore);
        userId = local.userId;
        signedIn = local.isAuthenticated;
        username = local.username || '';

        // The store is populated by App.svelte on load, but a direct hit on
        // /delete-account can land here first.
        try {
            const response = await fetch('/api/is_authenticated', { credentials: 'include' });
            if (response.ok) {
                const auth = await response.json();
                signedIn = true;
                userId = auth.userId || userId;
                username = auth.username && String(auth.username).toLowerCase() !== 'null'
                    ? auth.username : '';
            }
        } catch (error) {
            // Offline or signed out. Whatever is in the store stands.
        }
    });

    function clearLocalData() {
        try {
            LOCAL_KEYS.forEach(key => localStorage.removeItem(key));
        } catch (error) {
            // Private browsing can refuse storage access. The server side of
            // the deletion is what matters and has already happened.
            console.error('Could not clear local data:', error);
        }
    }

    async function deleteEverything() {
        step = 'deleting';
        errorMessage = '';

        try {
            const response = await fetch('/api/account/delete', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                // `confirm` is not a password and the person never types it.
                // It is there so nothing reaches that endpoint by accident.
                body: JSON.stringify({ confirm: 'DELETE', userId })
            });

            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                errorMessage = response.status === 401
                    ? 'Your session has expired. Sign in again and the delete button will work.'
                    : (body.error || 'Something went wrong and nothing was deleted.');
                step = 'error';
                return;
            }

            const body = await response.json();
            // The login survives if the Auth0 management credentials are not
            // set on the server. Saying so is better than a green tick that
            // is not quite true.
            identityKept = signedIn && body.identityRemoved === false;

            clearLocalData();
            userStore.logout();
            step = 'done';
        } catch (error) {
            console.error('Account deletion failed:', error);
            errorMessage = 'We could not reach the server, so nothing was deleted. Please try again.';
            step = 'error';
        }
    }

    function goHome() {
        // Signed-in people are signed out at the server, so a full reload is
        // the honest end state rather than a client-side route change.
        window.location.href = '/';
    }
</script>

<div class="u-page">
    <article class="wrap">
        {#if step === 'done'}
            <span class="eyebrow">Done</span>
            <h1>Your data is gone.</h1>
            <p class="lead">
                Your skin profile, your analyses, your routine and the products we
                had recommended to you have all been deleted. The copies that were
                on this device have been cleared too.
            </p>
            {#if identityKept}
                <Banner tone="caution" title="One thing is still being removed">
                    Your sign-in record is held by Auth0, who handle logging in, and
                    it is still being removed there. Email
                    <a href="mailto:privacy@skingenie.ai">privacy@skingenie.ai</a>
                    if you want confirmation once it is.
                </Banner>
            {/if}
            <p class="note">
                We kept a record of which products were opened from recommendations,
                with nothing linking it to you. It is how we find out when our
                ranking is wrong. The privacy policy says so, in those words.
            </p>
            <div class="actions">
                <Button on:click={goHome}>Back to home</Button>
            </div>

        {:else if step === 'error'}
            <span class="eyebrow">Account</span>
            <h1>Nothing was deleted.</h1>
            <Banner tone="danger" title="We could not complete the deletion">
                {errorMessage}
            </Banner>
            <div class="actions">
                <Button on:click={() => (step = 'confirming')}>Try again</Button>
                <Button variant="ghost" on:click={() => navigate('profile')}>Back to my account</Button>
            </div>

        {:else}
            <span class="eyebrow">Account</span>
            <h1>Delete {username ? `${username}'s` : 'your'} account</h1>
            <p class="lead">
                This removes your data from SkinGenie for good. There is no undo and
                no grace period, so please read what goes before you confirm.
            </p>

            {#if !signedIn && !userId}
                <Banner tone="info" title="There is nothing here to delete">
                    This browser has no SkinGenie data and you are not signed in. If
                    you have an account, sign in first and then come back. Here is
                    what deleting an account covers.
                </Banner>
            {/if}

            <Card>
                <h2>Deleted, permanently</h2>
                <ul class="list">
                    <li>Your skin profile: your concerns, skin type, age and the ingredients you avoid</li>
                    <li>Every face analysis on your account, and the progress photos saved with them</li>
                    <li>The routine you built, including products you added by hand</li>
                    <li>The products we had recommended and saved for you</li>
                    <li>Your sign-in record, so signing in again starts you from nothing</li>
                    <li>The analysis photo held in this browser, and anything else this device had cached</li>
                </ul>

                <h2 class="second">Kept, with nothing linking it to you</h2>
                <ul class="list">
                    <li>
                        Which products were opened from a set of recommendations, and
                        where they sat in the list. Your identifier is removed from
                        those rows, so they cannot be traced back to you. It is the
                        only way we learn that our ranking was wrong, and it is
                        described in the
                        <a href="/privacy">privacy policy</a>.
                    </li>
                </ul>
                <p class="note">
                    Your analysis photo was never on our servers to delete. It is
                    sent once, analysed, and not stored.
                    <a href="/privacy#photos">How we handle photos</a>.
                </p>
            </Card>

            {#if step === 'confirming'}
                <Banner tone="danger" title="Last check">
                    Deleting is immediate and cannot be undone.
                </Banner>
                <div class="actions">
                    <Button variant="danger" on:click={deleteEverything}>
                        Yes, delete everything
                    </Button>
                    <Button variant="ghost" on:click={() => (step = 'intro')}>Keep my account</Button>
                </div>
            {:else if step === 'deleting'}
                <div class="actions">
                    <Button variant="danger" loading={true} busyLabel="Deleting">Deleting</Button>
                </div>
            {:else if !signedIn && !userId}
                <div class="actions">
                    <Button variant="secondary" on:click={() => navigate('home')}>Back to home</Button>
                </div>
            {:else}
                <div class="actions">
                    <Button variant="danger" on:click={() => (step = 'confirming')}>
                        Delete my account
                    </Button>
                    <Button variant="ghost" on:click={() => navigate(signedIn ? 'profile' : 'home')}>
                        Cancel
                    </Button>
                </div>
            {/if}
        {/if}
    </article>
</div>

<style>
    .wrap {
        max-width: var(--prose-max);
        margin: 0 auto;
    }

    .eyebrow {
        display: block;
        color: var(--rose);
        font-size: var(--text-2xs);
        letter-spacing: var(--track-eyebrow);
        text-transform: uppercase;
        margin-bottom: var(--space-4);
    }

    h1 {
        margin: 0;
        font-family: var(--font-display);
        font-weight: var(--weight-regular);
        font-size: var(--text-4xl);
        line-height: var(--leading-tight);
        color: var(--cream);
    }

    h2 {
        margin: 0 0 var(--space-4);
        font-family: var(--font-body);
        font-size: var(--text-2xs);
        font-weight: var(--weight-medium);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
        color: var(--text-muted);
    }

    h2.second {
        margin-top: var(--space-8);
        padding-top: var(--space-6);
        border-top: 1px solid var(--border);
    }

    .lead {
        max-width: var(--prose-max);
        margin: var(--space-4) 0 var(--space-10);
        color: var(--text-muted);
        line-height: var(--leading-relaxed);
    }

    .list {
        margin: 0;
        padding-left: var(--space-6);
        color: var(--cream);
        font-size: var(--text-sm);
        line-height: var(--leading-relaxed);
    }

    .list li + li {
        margin-top: var(--space-2);
    }

    .note {
        margin: var(--space-6) 0 0;
        color: var(--text-muted);
        font-size: var(--text-xs);
        line-height: var(--leading-relaxed);
    }

    .actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-4);
        margin-top: var(--space-10);
    }
</style>
